import {
  SquaresFourIcon,
  CubeIcon,
  ShareNetworkIcon,
  ArrowsLeftRightIcon,
  PackageIcon,
  TruckIcon,
  BuildingsIcon,
  QrCodeIcon,
  ChartBarIcon,
  WarehouseIcon,
  ClipboardTextIcon,
  ShoppingCartIcon,
  ArrowsDownUpIcon,
  ClipboardIcon,
  CameraIcon,
  LightningIcon,
  BicycleIcon,
  CurrencyDollarIcon,
} from '@phosphor-icons/react';
import { PATHS_NAMES } from '../pathnames';
import { IResource, Role } from '../types';

const WAREHOUSE_EXECUTIVE_AND_ABOVE: Array<Role> = [
  'WAREHOUSE_ADMIN',
  'WAREHOUSE_MANAGER',
  'WAREHOUSE_EXECUTIVE',
];
const WAREHOUSE_MANAGER_AND_ABOVE: Array<Role> = ['WAREHOUSE_ADMIN', 'WAREHOUSE_MANAGER'];
const WAREHOUSE_LOGISTICS_ROLES: Array<Role> = [
  'RIDER',
  ...WAREHOUSE_EXECUTIVE_AND_ABOVE,
  'SUPER_ADMIN',
];

export const getWarehouseResources = (isAllFacilities?: boolean): Array<IResource> => [
  {
    name: 'dashboard',
    list: '/',
    meta: {
      label: 'Dashboard',
      icon: <SquaresFourIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'packages',
    list: '/packages',
    show: '/packages/show/:id',
    meta: {
      label: 'Packages',
      icon: <CubeIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'ordercontrolcenter',
    list: '/ordercontrolcenter',
    meta: {
      label: 'Control Center',
      icon: <ShareNetworkIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'returns',
    list: '/returns',
    show: '/returns/show/:id',
    meta: {
      label: 'Returns',
      icon: <ArrowsLeftRightIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.RETURN_COURIER_EXCEPTIONS,
    list: '/returns/exceptions/cancelled-awb-movement',
    meta: {
      label: 'Return Courier Exceptions',
      icon: <ArrowsLeftRightIcon />,
      masterRoles: WAREHOUSE_MANAGER_AND_ABOVE,
    },
  },
  {
    name: 'refunds-onhold',
    list: '/refunds-onhold',
    show: '/refunds/show/:id',
    meta: {
      label: 'Refunds Onhold',
      icon: <CurrencyDollarIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'inventory',
    list: '/inventory',
    meta: {
      label: 'Inventory',
      icon: <PackageIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: 'vendormanagement',
    meta: {
      label: 'Vendors',
      icon: <TruckIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: 'vendors',
    meta: {
      parent: 'vendormanagement',
      label: 'Vendors',
      icon: <BuildingsIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
    list: '/vendors',
    show: '/vendors/show/:id',
  },
  {
    name: 'skus',
    list: '/skus',
    meta: {
      parent: 'vendormanagement',
      label: 'Vendor Catalog',
      icon: <QrCodeIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: PATHS_NAMES.SKU_ENQUIRY_HISTORY,
    list: '/vendors/sku-enquiry-history',
    meta: {
      parent: 'vendormanagement',
      label: 'SKU Enquiry History',
      icon: <ChartBarIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: 'inbound',
    meta: {
      label: 'Inbound',
      icon: <WarehouseIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'grn',
    list: '/grn',
    show: '/grn/show/:id',
    edit: '/grn/edit/:id',
    meta: {
      parent: 'inbound',
      label: 'GRN',
      icon: <ClipboardTextIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        edit: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        export_barcode: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        receive: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        import: ['IMPORT_ADMIN'],
      },
    },
  },
  {
    name: 'mtopurchaseorders',
    list: '/mtopurchaseorders',
    meta: {
      parent: 'inbound',
      label: 'MTO Purchase Orders',
      icon: <ShoppingCartIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  ...(isAllFacilities
    ? [
        {
          name: PATHS_NAMES.INCOMING_STOCK_TRANSFERS,
          list: '/incoming-stock-transfers',
          meta: {
            parent: 'inbound',
            label: 'Stock Transfers',
            icon: <ArrowsDownUpIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]
    : [
        {
          name: PATHS_NAMES.INCOMING_STOCK_TRANSFERS,
          list: '/incoming-stock-transfers',
          meta: {
            parent: 'inbound',
            label: 'Incoming Stock Transfers',
            icon: <ArrowsDownUpIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
        {
          name: PATHS_NAMES.OUTGOING_STOCK_TRANSFERS,
          list: '/outgoing-stock-transfers',
          meta: {
            parent: 'inbound',
            label: 'Outgoing Stock Transfers',
            icon: <TruckIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]),
  ...(isAllFacilities
    ? []
    : [
        {
          name: PATHS_NAMES.INCOMING_MANIFESTS,
          list: '/incoming-manifests',
          meta: {
            parent: 'inbound',
            label: 'Incoming Manifests',
            icon: <ClipboardTextIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
        {
          name: PATHS_NAMES.OUTGOING_MANIFESTS,
          list: '/outgoing-manifests',
          meta: {
            parent: 'inbound',
            label: 'Outgoing Manifests',
            icon: <ClipboardIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]),
  {
    name: PATHS_NAMES.MANIFESTS,
    create: '/manifests/create',
    show: '/manifests/:id/show',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.GATEPASS_ORDERS,
    show: '/gatepass-orders/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'packages',
    show: '/packages/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'lineitems',
    show: '/lineitems/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'orders',
    show: '/orders/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'refunds',
    show: '/refunds/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.FACILITIES,
    show: '/facilities/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.B2B_CUSTOMERS,
    show: '/b2b-customer/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  { name: 'skuupload' },
  { name: 'pincode' },
  {
    name: 'store-images',
    list: '/utility/store-images',
    meta: {
      parent: 'utility',
      label: 'Store Images',
      icon: <CameraIcon />,
      masterRoles: ['*'],
    },
  },
  {
    name: PATHS_NAMES.DASH_ORDERS,
    list: '/dash-orders',
    meta: {
      label: 'SELF Orders',
      icon: <LightningIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.LOGISTICS,
    list: '/logistics',
    meta: {
      label: 'Logistics',
      icon: <TruckIcon />,
      masterRoles: WAREHOUSE_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.MY_RIDES,
    list: '/logistics/my-rides',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'My Rides',
      icon: <BicycleIcon />,
      masterRoles: WAREHOUSE_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.CASH_HANDOVER,
    list: '/logistics/cash-handover',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Cash Handover',
      icon: <CurrencyDollarIcon />,
      masterRoles: [...WAREHOUSE_EXECUTIVE_AND_ABOVE, 'SUPER_ADMIN'],
    },
  },
  {
    name: PATHS_NAMES.RIDER_ATTENDANCE_QR,
    list: '/logistics/attendance-qr',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Attendance QR',
      icon: <QrCodeIcon />,
      masterRoles: [...WAREHOUSE_EXECUTIVE_AND_ABOVE, 'SUPER_ADMIN'],
    },
  },
  {
    name: PATHS_NAMES.RIDER_HEALTH,
    list: '/logistics/rider-health',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Rider Health',
      icon: <BicycleIcon />,
      masterRoles: [...WAREHOUSE_EXECUTIVE_AND_ABOVE, 'SUPER_ADMIN'],
    },
  },
];

// Backward compatibility - keep the old static array
export const WAREHOUSE_RESOURCES: Array<IResource> = getWarehouseResources(false);
